import React from 'react';
import { Modal, message, Dropdown, Button, type MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  checkCanEdit,
  checkCanDelete,
  checkCanClone,
  convertToFilterInfo,
  checkCanEnable,
} from '../utils';
import { gerResponseErrorMessage } from '../../../utils';
import * as atoms from '../../atom';
import * as Types from '../../../types';

interface Props {
  savedFilter: Types.SavedFilter;
}

export const Operations = ({ savedFilter }: Props) => {
  const [modal, contextHolder] = Modal.useModal();
  const dataSource = useAtomValue(atoms.dataSource);
  const callbacks = useAtomValue(atoms.callbacks);
  const user = useAtomValue(atoms.user);
  const disableCreateFilter = useAtomValue(atoms.disableCreateFilter);
  const setAsyncLoadingMask = useSetAtom(atoms.asyncLoadingMask);
  const setFilterEditor = useSetAtom(atoms.filterEditor);
  const setFilterClone = useSetAtom(atoms.filterClone);

  const permissionCheckParams = {
    user,
    savedFilter,
    dataSource,
    disableCreateFilter,
  };
  const allowEdit = checkCanEdit(permissionCheckParams);
  const allowEnable = checkCanEnable(permissionCheckParams);
  const allowDelete = checkCanDelete(permissionCheckParams);
  const allowClone = checkCanClone(permissionCheckParams);

  const handleEdit = async () => {
    if (allowEdit) {
      setFilterEditor(convertToFilterInfo(savedFilter));
    }
  };

  const handleEnable = async () => {
    if (allowEnable) {
      modal.confirm({
        title: `Enable ${savedFilter.name}?`,
        content: `Once you enable the filter, Conviva will start computing the data for the filter. Please note that once you enable a filter, you won't be able to disable it.`,
        onOk: async () => {
          setAsyncLoadingMask(true);
          try {
            await callbacks.onEnableFilter?.(savedFilter.fid, dataSource);
            message.success(`Enable ${savedFilter.name} successfully!`);
          } catch (e: any) {
            const errMsg = gerResponseErrorMessage(e) || `Failed to enable ${savedFilter.name}`;
            message.error(errMsg);
          }
          setAsyncLoadingMask(false);
        },
      });
    }
  };

  const handleDelete = async () => {
    if (allowDelete) {
      modal.confirm({
        title: `Delete ${savedFilter.name}?`,
        content: `Do you want to delete this filter?`,
        onOk: async () => {
          setAsyncLoadingMask(true);
          try {
            await callbacks.onDeleteFilter?.(savedFilter, dataSource);
            message.success(`Delete ${savedFilter.name} successfully!`);
          } catch (e: any) {
            const errMsg = gerResponseErrorMessage(e) || `Failed to delete ${savedFilter.name}`;
            message.error(errMsg);
          }
          setAsyncLoadingMask(false);
        },
      });
    }
  };

  const handleClone = () => {
    if (allowClone) {
      const newFilterInfo = convertToFilterInfo(savedFilter);
      newFilterInfo.filter.name = `${newFilterInfo.filter.name} (copy)`;
      newFilterInfo.filter.hidden = user.hidden;
      setFilterClone(newFilterInfo);
    }
  };

  const dropdownMenuItems = [
    allowEdit && {
      key: 'edit',
      label: 'Edit',
    },
    allowClone && {
      key: 'clone',
      label: 'Clone',
    },
    allowEnable && {
      key: 'enable',
      label: 'Enable',
    },
    allowDelete && {
      key: 'delete',
      label: 'Delete',
      disabled: !allowEdit,
    },
  ].filter(Boolean);

  if (!dropdownMenuItems.length) return null;
  return (
    <>
      {contextHolder}
      <Dropdown
        className="operations"
        placement="bottomRight"
        trigger={['click']}
        menu={{
          items: dropdownMenuItems as MenuProps['items'],
          onClick: ({ key }) => {
            if (key === 'edit') {
              return handleEdit();
            }

            if (key === 'clone') {
              return handleClone();
            }

            if (key === 'delete') {
              return handleDelete();
            }

            if (key === 'enable') {
              return handleEnable();
            }
          },
        }}
      >
        <Button type="text" icon={<MoreOutlined />} style={{ fontSize: 16 }} />
      </Dropdown>
    </>
  );
};
